import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { Card, Spinner, fmtNum, fmtKg } from "@/components/ui/Primitives";
import { api } from "@/lib/api";
import {
  Package, Users, Scale, Truck, DollarSign, TrendingUp, TrendingDown, Minus,
  AlertTriangle, Info, Sprout, BarChart3, Layers, Tag, Activity,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
  AreaChart, Area, PieChart, Pie, Cell,
} from "recharts";

// ---------- helpers ----------
const fmtBRL = (v) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 }).format(Number(v || 0));
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("pt-BR", { day: "2-digit", month: "short" }); } catch { return iso; } };

const PERIODS = [
  { label: "7 dias", value: 7 },
  { label: "30 dias", value: 30 },
  { label: "90 dias", value: 90 },
  { label: "12 meses", value: 365 },
];

const COLORS_CAT = ["#4A5D23", "#C97B3E", "#A8D550", "#7A9E2E", "#E8A24E"];

// ---------- componentes ----------
function DeltaBadge({ pct }) {
  if (pct === null || pct === undefined) return <span className="text-xs text-ink-400">—</span>;
  const isUp = pct > 0;
  const isDown = pct < 0;
  const Icon = isUp ? TrendingUp : isDown ? TrendingDown : Minus;
  const cls = isUp ? "text-emerald-700 bg-emerald-50" : isDown ? "text-red-700 bg-red-50" : "text-ink-500 bg-stone-100";
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${cls}`}>
      <Icon size={12} /> {Math.abs(pct)}%
    </span>
  );
}

function KpiCard({ icon: Icon, label, value, hint, delta, tone = "brand", testid, sub }) {
  const tones = {
    brand: "text-brand-500 bg-brand-50",
    warn: "text-warn bg-amber-50",
    danger: "text-danger bg-red-50",
    ink: "text-ink-700 bg-ink-200/50",
    ok: "text-emerald-700 bg-emerald-50",
    ocre: "text-orange-700 bg-orange-50",
  };
  return (
    <Card className="p-5 hover:-translate-y-0.5 transition-transform" data-testid={testid}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="text-xs font-bold uppercase tracking-[0.1em] text-ink-500">{label}</div>
          <div className="font-mono text-3xl font-semibold text-ink-900 mt-2 tracking-tight truncate">{value}</div>
          {(hint || delta !== undefined) && (
            <div className="flex items-center gap-2 mt-1.5">
              {delta !== undefined && <DeltaBadge pct={delta} />}
              {hint && <div className="text-xs text-ink-500">{hint}</div>}
            </div>
          )}
          {sub && <div className="text-xs text-ink-400 mt-1">{sub}</div>}
        </div>
        <div className={`w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0 ${tones[tone]}`}>
          <Icon size={20} />
        </div>
      </div>
    </Card>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(30);

  useEffect(() => {
    setLoading(true);
    api.get(`/dashboard/stats?period=${period}`).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [period]);

  if (loading || !data) return <Layout title="Dashboard"><div className="flex justify-center py-20"><Spinner size={28} /></div></Layout>;

  const s = data;
  const counts = s.invoices_by_status || {};
  const catData = Object.entries(s.caixas_by_cat || {})
    .filter(([, v]) => v > 0)
    .map(([k, v]) => ({ name: k.toUpperCase(), value: v }));

  return (
    <Layout
      title="Dashboard"
      subtitle="Visão executiva da operação"
      actions={
        <div className="flex items-center gap-1 p-1 bg-white border border-tertiary rounded-lg" data-testid="period-selector">
          {PERIODS.map((p) => (
            <button
              key={p.value}
              onClick={() => setPeriod(p.value)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${period === p.value ? "bg-brand-500 text-white" : "text-ink-500 hover:bg-stone-100"}`}
              data-testid={`period-${p.value}`}
            >
              {p.label}
            </button>
          ))}
        </div>
      }
    >
      {/* Alertas */}
      {(s.alertas || []).length > 0 && (
        <div className="mb-6 space-y-2" data-testid="alerts-section">
          {s.alertas.map((a, i) => (
            <Link
              key={i}
              to={a.path || "#"}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg border transition-colors hover:shadow-sm ${a.tipo === "warn" ? "bg-amber-50 border-amber-200 text-amber-900" : "bg-sky-50 border-sky-200 text-sky-900"}`}
              data-testid={`alert-${i}`}
            >
              {a.tipo === "warn" ? <AlertTriangle size={18} className="flex-shrink-0" /> : <Info size={18} className="flex-shrink-0" />}
              <span className="text-sm flex-1">{a.texto}</span>
              <span className="text-xs opacity-70">Abrir →</span>
            </Link>
          ))}
        </div>
      )}

      {/* KPIs principais — 4 cards com deltas */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        <KpiCard
          icon={DollarSign}
          label={`Receita ${period}d`}
          value={fmtBRL(s.revenue_finalized)}
          delta={s.revenue_delta_pct}
          hint={`vs ${fmtBRL(s.revenue_prev)} anterior`}
          tone="brand"
          testid="kpi-revenue"
        />
        <KpiCard
          icon={Truck}
          label={`Saídas ${period}d`}
          value={fmtNum(s.shipments_finalized_period, 0)}
          hint={`Ticket médio: ${fmtBRL(s.ticket_medio)}`}
          tone="ocre"
          testid="kpi-shipments"
        />
        <KpiCard
          icon={Package}
          label={`Caixas ${period}d`}
          value={fmtNum(s.caixas_period, 0)}
          delta={s.caixas_prev > 0 ? Math.round((s.caixas_period - s.caixas_prev) / s.caixas_prev * 1000) / 10 : null}
          hint={`vs ${fmtNum(s.caixas_prev, 0)} anterior`}
          tone="ok"
          testid="kpi-caixas"
        />
        <KpiCard
          icon={Sprout}
          label="Aproveitamento"
          value={`${s.aproveitamento_pct || 0}%`}
          hint={`Quebra média: ${fmtKg(s.quebra_media_kg)}`}
          tone={s.aproveitamento_pct >= 90 ? "ok" : s.aproveitamento_pct >= 80 ? "warn" : "danger"}
          testid="kpi-aproveitamento"
        />
      </div>

      {/* KPIs operacionais — 4 cards secundários */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mt-5">
        <KpiCard
          icon={Layers}
          label="Estoque em pallets"
          value={fmtNum(s.pallets_fechado, 0)}
          sub="Pallets fechados prontos p/ expedir"
          tone="ink"
          testid="kpi-pallets-estoque"
        />
        <KpiCard
          icon={Activity}
          label="Em andamento"
          value={counts.em_beneficiamento || 0}
          sub={`NFs em beneficiamento • ${counts.bruto || 0} brutas aguardando`}
          tone="warn"
          testid="kpi-em-andamento"
        />
        <KpiCard
          icon={Users}
          label="Clientes / Fornecedores"
          value={`${s.clientes_count} / ${s.suppliers_count}`}
          sub="Cadastros ativos"
          tone="ink"
          testid="kpi-cadastros"
        />
        <KpiCard
          icon={Scale}
          label={`Kg movimentados ${period}d`}
          value={fmtKg(s.total_kg_period)}
          delta={s.total_kg_prev > 0 ? Math.round((s.total_kg_period - s.total_kg_prev) / s.total_kg_prev * 1000) / 10 : null}
          hint={`vs ${fmtKg(s.total_kg_prev)} anterior`}
          tone="ok"
          testid="kpi-kg"
        />
      </div>

      {/* Row 1 — Gráfico principal (receita) + Distribuição de categoria */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mt-6">
        <Card className="lg:col-span-2 p-6">
          <div className="flex items-baseline justify-between mb-4">
            <div>
              <h3 className="font-heading font-bold text-ink-900 flex items-center gap-2">
                <BarChart3 size={16} className="text-brand-500" /> Receita ao longo do tempo
              </h3>
              <p className="text-xs text-ink-500 mt-0.5">Saídas finalizadas nos últimos {period} dias</p>
            </div>
            <div className="text-xs text-ink-500 font-mono">R$</div>
          </div>
          <div className="h-72" data-testid="chart-receita">
            {(s.receita_series || []).length === 0 ? (
              <div className="h-full flex items-center justify-center text-ink-300 text-sm">Sem receita registrada no período</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={s.receita_series}>
                  <defs>
                    <linearGradient id="grad-receita" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#4A5D23" stopOpacity={0.4} />
                      <stop offset="100%" stopColor="#4A5D23" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                  <XAxis dataKey="date" tickFormatter={fmtDate} stroke="#57534E" tick={{ fontSize: 10 }} />
                  <YAxis stroke="#57534E" tick={{ fontSize: 10 }} tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
                  <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E7E5E4", fontSize: 12 }} formatter={(v) => fmtBRL(v)} labelFormatter={fmtDate} />
                  <Area type="monotone" dataKey="receita" stroke="#4A5D23" strokeWidth={2} fill="url(#grad-receita)" name="Receita" />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>

        <Card className="p-6">
          <div className="mb-4">
            <h3 className="font-heading font-bold text-ink-900 flex items-center gap-2">
              <Tag size={16} className="text-brand-500" /> Distribuição por categoria
            </h3>
            <p className="text-xs text-ink-500 mt-0.5">Caixas expedidas no período</p>
          </div>
          <div className="h-56" data-testid="chart-cat">
            {catData.length === 0 ? (
              <div className="h-full flex items-center justify-center text-ink-300 text-sm">Sem saídas no período</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={catData} dataKey="value" nameKey="name" innerRadius={45} outerRadius={80} paddingAngle={2}>
                    {catData.map((_, i) => <Cell key={i} fill={COLORS_CAT[i % COLORS_CAT.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E7E5E4", fontSize: 12 }} formatter={(v) => `${fmtNum(v, 0)} cx`} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2 mt-2">
            {catData.map((c, i) => (
              <div key={c.name} className="flex items-center gap-2 text-xs">
                <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: COLORS_CAT[i % COLORS_CAT.length] }} />
                <span className="font-medium text-ink-700">{c.name}</span>
                <span className="text-ink-500 font-mono ml-auto">{fmtNum(c.value, 0)}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Row 2 — Bruto vs Beneficiado + Tipos de cebola */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mt-6">
        <Card className="lg:col-span-2 p-6">
          <div className="flex items-baseline justify-between mb-4">
            <div>
              <h3 className="font-heading font-bold text-ink-900 flex items-center gap-2">
                <Scale size={16} className="text-orange-600" /> Fluxo de peso
              </h3>
              <p className="text-xs text-ink-500 mt-0.5">Bruto recebido × Embalado (kg) — {period}d</p>
            </div>
          </div>
          <div className="h-64" data-testid="chart-fluxo">
            {(s.series || []).length === 0 ? (
              <div className="h-full flex items-center justify-center text-ink-300 text-sm">Sem registros no período</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={s.series}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                  <XAxis dataKey="date" tickFormatter={fmtDate} stroke="#57534E" tick={{ fontSize: 10 }} />
                  <YAxis stroke="#57534E" tick={{ fontSize: 10 }} tickFormatter={(v) => `${(v/1000).toFixed(1)}t`} />
                  <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E7E5E4", fontSize: 12 }} formatter={(v) => `${fmtNum(v, 0)} kg`} labelFormatter={fmtDate} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="bruto" fill="#4A5D23" name="Bruto" radius={[4,4,0,0]} />
                  <Bar dataKey="beneficiado" fill="#C97B3E" name="Embalado" radius={[4,4,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>

        <Card className="p-6">
          <div className="mb-4">
            <h3 className="font-heading font-bold text-ink-900 flex items-center gap-2">
              <Sprout size={16} className="text-emerald-600" /> Por tipo de cebola
            </h3>
            <p className="text-xs text-ink-500 mt-0.5">Caixas beneficiadas no período</p>
          </div>
          <div className="space-y-2.5" data-testid="tipos-cebola">
            {(s.tipos_venda || []).length === 0 ? (
              <div className="text-sm text-ink-400 text-center py-8">Sem dados no período</div>
            ) : (
              (s.tipos_venda || []).slice(0, 6).map((t) => {
                const maxV = Math.max(...s.tipos_venda.map(x => x.caixas));
                const pct = maxV > 0 ? (t.caixas / maxV) * 100 : 0;
                return (
                  <div key={t.tipo}>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="font-medium text-ink-700 capitalize">{t.tipo}</span>
                      <span className="text-ink-500 font-mono">{fmtNum(t.caixas, 0)} cx</span>
                    </div>
                    <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-emerald-600 to-emerald-400 rounded-full" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </Card>
      </div>

      {/* Row 3 — Tops (clientes + fornecedores) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mt-6">
        <Card className="p-6">
          <div className="mb-4">
            <h3 className="font-heading font-bold text-ink-900 flex items-center gap-2">
              <Users size={16} className="text-brand-500" /> Top 5 clientes
            </h3>
            <p className="text-xs text-ink-500 mt-0.5">Maior receita no período</p>
          </div>
          <div className="space-y-2" data-testid="top-clientes">
            {(s.top_clientes || []).length === 0 ? (
              <div className="text-sm text-ink-400 text-center py-8">Sem saídas no período</div>
            ) : (
              (s.top_clientes || []).map((c, i) => {
                const max = s.top_clientes[0]?.valor || 1;
                const pct = (c.valor / max) * 100;
                return (
                  <div key={c.nome} className="flex items-center gap-3 p-2 rounded-lg hover:bg-stone-50">
                    <div className={`w-6 h-6 rounded-md flex items-center justify-center text-xs font-bold ${i === 0 ? "bg-amber-100 text-amber-800" : i === 1 ? "bg-stone-200 text-ink-700" : "bg-stone-100 text-ink-500"}`}>{i + 1}</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-ink-900 truncate">{c.nome}</div>
                      <div className="h-1.5 bg-stone-100 rounded-full overflow-hidden mt-1">
                        <div className="h-full bg-brand-500 rounded-full" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                    <div className="font-mono text-sm font-semibold text-brand-500 tabular-nums">{fmtBRL(c.valor)}</div>
                  </div>
                );
              })
            )}
          </div>
        </Card>

        <Card className="p-6">
          <div className="mb-4">
            <h3 className="font-heading font-bold text-ink-900 flex items-center gap-2">
              <Users size={16} className="text-orange-600" /> Top 5 fornecedores
            </h3>
            <p className="text-xs text-ink-500 mt-0.5">Maior volume recebido no período</p>
          </div>
          <div className="space-y-2" data-testid="top-fornecedores">
            {(s.top_fornecedores || []).length === 0 ? (
              <div className="text-sm text-ink-400 text-center py-8">Sem entradas no período</div>
            ) : (
              (s.top_fornecedores || []).map((f, i) => {
                const max = s.top_fornecedores[0]?.kg || 1;
                const pct = (f.kg / max) * 100;
                return (
                  <div key={f.nome} className="flex items-center gap-3 p-2 rounded-lg hover:bg-stone-50">
                    <div className={`w-6 h-6 rounded-md flex items-center justify-center text-xs font-bold ${i === 0 ? "bg-amber-100 text-amber-800" : i === 1 ? "bg-stone-200 text-ink-700" : "bg-stone-100 text-ink-500"}`}>{i + 1}</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-ink-900 truncate">{f.nome}</div>
                      <div className="h-1.5 bg-stone-100 rounded-full overflow-hidden mt-1">
                        <div className="h-full bg-orange-500 rounded-full" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                    <div className="font-mono text-sm font-semibold text-orange-700 tabular-nums">{fmtKg(f.kg)}</div>
                  </div>
                );
              })
            )}
          </div>
        </Card>
      </div>
    </Layout>
  );
}
